import { HTMLAttributes } from 'react'
import { ReactComponent as HomeIcon } from '../assets/shared/icons/sidenav/home.svg'
import { ReactComponent as BuildingIcon } from '../assets/shared/icons/sidenav/building.svg'
import { ReactComponent as BoxIcon } from '../assets/shared/icons/sidenav/box.svg'
import { ReactComponent as TagIcon } from '../assets/shared/icons/sidenav/tag.svg'
import { ReactComponent as FileTextIcon } from '../assets/shared/icons/sidenav/file-text.svg'
import { ReactComponent as NoteEditIcon } from '../assets/shared/icons/sidenav/note_edit.svg'
import { ReactComponent as NoteSearchIcon } from '../assets/shared/icons/sidenav/note_search.svg'
import { ReactComponent as CreditCardIcon } from '../assets/shared/icons/sidenav/credit-card.svg'
import { ReactComponent as UserGroupIcon } from '../assets/shared/icons/sidenav/user_group.svg'
import { ReactComponent as SettingsIcon } from '../assets/shared/icons/sidenav/settings.svg'
import { ReactComponent as HelpCircleIcon } from '../assets/shared/icons/sidenav/help-circle.svg'
import { ReactComponent as NotificationsIcon } from '../assets/shared/icons/notifications.svg'
import { ReactComponent as ShareIcon } from '../assets/shared/icons/share.svg'
import { ReactComponent as TaskIcon } from '../assets/shared/icons/sidenav/task.svg'
import { ReactComponent as CustomerIcon } from '../assets/shared/icons/sidenav/customer.svg'

/**
 * Titled group the sidebar renders as a section header. Only `settings` exists
 * today: the sidebar lists every ungrouped item flat, then a single "Settings"
 * header with its items at the bottom. The type stays a union so more sections
 * can be added later without a wider change.
 */
export type MenuSectionKey = 'settings'

/**
 * Section id → label, icon and display order (drives the sidebar grouping).
 * The sidebar renders each section as a collapsible entry styled exactly like a
 * normal menu item, so the icon is required.
 */
export const MENU_SECTIONS: {
  id: MenuSectionKey,
  title: string,
  icon: React.FC<HTMLAttributes<SVGElement>>
}[] = [{ id: 'settings', title: 'Settings', icon: SettingsIcon }]

export type MenuItem = {
  id: string,
  name: string,
  link?: string,
  icon: React.FC<HTMLAttributes<SVGElement>>,
  /**
   * Titled group this entry belongs to (see `MENU_SECTIONS`). Items without a
   * section render flat, in order; sectioned items render under their header at
   * the bottom of the list.
   */
  section?: MenuSectionKey,
  children?: MenuItem[],
  /**
   * Roles allowed to see the entry. Spring Security style — `ROLE_DEVELOPER`,
   * `ROLE_ADMIN`, etc. Omit (or empty) to skip role gating.
   */
  allowedRoles?: string[],
  /**
   * Permission codes — visible if the user has ANY of them. Omit (or empty) to
   * skip permission gating. When BOTH `allowedRoles` and `requiredPermissions`
   * are set, the user must satisfy both axes.
   */
  requiredPermissions?: string[],
  /**
   * Module code (e.g. `PROJECT`, `LEAD`) — entry hidden when the module is
   * disabled in `module_config`. ANDed with the role/permission gates.
   */
  requiredModule?: string,
  /**
   * Extra route paths that should also light this menu entry. Useful when a
   * single sidebar item fronts a group of related pages reachable via in-page
   * tabs (e.g. Communication → /communications/*).
   */
  matchPaths?: string[]
}

const ADMIN_LIKE = [
  'ROLE_DEVELOPER',
  'ROLE_ADMIN',
  'ROLE_SUPER_ADMIN',
  'ROLE_SALES_EXEC'
]

// Ungrouped items render flat in this order; the `settings`-tagged items render
// last under a single "Settings" header:
//   flat  — Dashboard, Projects, Plots, Bookings, Payments, Customers, Brokers,
//           Leads, Quotes, Commissions, Tasks, Communication, Reports, Documents,
//           Invite User, Users, Roles, Module config, Form builder
//   ── Settings ── Communication profile & settings, Project Settings, My profile
const menuItems: MenuItem[] = [
  // ── 1. Overview ─────────────────────────────────────────────────────
  {
    id: 'dashboard',
    name: 'Dashboard',
    link: '/dashboard',
    icon: HomeIcon
  },

  // ── 2. Master data: Projects → Plots ───────────────────────────────
  {
    id: 'projects',
    name: 'Projects',
    link: '/projects',
    icon: BuildingIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: [
      'project.read',
      'project.read_assigned',
      'project.read_own'
    ],
    requiredModule: 'PROJECT'
  },
  {
    id: 'plots',
    name: 'Plots',
    link: '/plots',
    icon: BoxIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['plot.read'],
    requiredModule: 'PLOT'
  },
  {
    id: 'bookings',
    name: 'Bookings',
    link: '/bookings',
    icon: NoteEditIcon,
    allowedRoles: [
      'ROLE_DEVELOPER',
      'ROLE_ADMIN',
      'ROLE_SUPER_ADMIN',
      'ROLE_SALES_EXEC',
      'ROLE_ACCOUNTANT'
    ],
    requiredPermissions: ['booking.read', 'booking.read_all'],
    requiredModule: 'BOOKING'
  },
  {
    id: 'payments',
    name: 'Payments',
    link: '/payments',
    icon: CreditCardIcon,
    // Spelled out rather than reusing ADMIN_LIKE: accountants get the money
    // pages only, so this list has to differ from every other entry's.
    allowedRoles: [
      'ROLE_DEVELOPER',
      'ROLE_ADMIN',
      'ROLE_SUPER_ADMIN',
      'ROLE_SALES_EXEC',
      'ROLE_ACCOUNTANT'
    ],
    requiredPermissions: ['payment.read'],
    requiredModule: 'PAYMENT'
  },

  // ── 3. People: Customers (buyers) → Brokers (sellers) ──────────────
  {
    id: 'customers',
    name: 'Customers',
    link: '/customers',
    icon: CustomerIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['customer.read', 'customer.read_own'],
    requiredModule: 'CUSTOMER'
  },
  {
    id: 'brokers',
    name: 'Brokers',
    link: '/brokers',
    icon: UserGroupIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['broker.read'],
    requiredModule: 'BROKER'
  },

  // ── 4. Pipeline: Leads → Quotes → Commissions ──────────────────────
  {
    id: 'leads',
    name: 'Leads',
    link: '/leads',
    icon: TagIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['lead.read', 'lead.read_all'],
    requiredModule: 'LEAD'
  },
  {
    id: 'quotes',
    name: 'Quotes',
    link: '/quotes',
    icon: FileTextIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['quote.read'],
    requiredModule: 'QUOTE'
  },
  {
    id: 'commissions',
    name: 'Commissions',
    link: '/commissions',
    icon: CreditCardIcon,
    allowedRoles: ['ROLE_SUPER_ADMIN'],
    requiredPermissions: ['commission.read', 'commission.read_own'],
    requiredModule: 'BROKER'
  },

  // ── 5. Operations: Tasks → Communication ───────────────────────────
  {
    id: 'tasks',
    name: 'Tasks',
    link: '/tasks',
    icon: TaskIcon,
    allowedRoles: ADMIN_LIKE,
    requiredPermissions: ['task.read'],
    requiredModule: 'TASK'
  },

  // ── 6. Insights ────────────────────────────────────────────────────
  {
    id: 'reports',
    name: 'Reports',
    link: '/reports',
    icon: NoteSearchIcon,
    allowedRoles: [
      'ROLE_DEVELOPER',
      'ROLE_ADMIN',
      'ROLE_SUPER_ADMIN',
      'ROLE_SALES_EXEC',
      'ROLE_ACCOUNTANT'
    ],
    requiredPermissions: [
      'report.sales',
      'report.collection',
      'report.broker',
      'report.documents',
      'report.legal',
      'report.pending'
    ],
    requiredModule: 'REPORT'
  },

  // ── 7. Documents (super-admin only) ────────────────────────────────
  {
    id: 'documents',
    name: 'Documents',
    link: '/documents',
    icon: FileTextIcon,
    // Visible only to Super Admin — document upload for other users lives on
    // the Customer / Plot detail pages instead.
    allowedRoles: ['ROLE_SUPER_ADMIN'],
    requiredPermissions: ['document.read'],
    requiredModule: 'DOCUMENT'
  },

  // ── 8. Admin / org management ──────────────────────────────────────
  {
    id: 'invite-sales-exec',
    name: 'Invite User',
    link: '/invitations/sales-exec',
    icon: ShareIcon,
    allowedRoles: ['ROLE_DEVELOPER']
  },
  {
    id: 'users',
    name: 'Users',
    link: '/admin/users',
    icon: UserGroupIcon,
    // Sales exec is intentionally absent — they can't see the user directory.
    allowedRoles: ['ROLE_SUPER_ADMIN']
  },
  {
    id: 'roles',
    name: 'Roles',
    link: '/admin/roles',
    icon: SettingsIcon,
    allowedRoles: ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']
  },
  {
    id: 'admin-module-config',
    name: 'Module configuration',
    link: '/admin/module-config',
    icon: SettingsIcon,
    allowedRoles: ['ROLE_SUPER_ADMIN'],
    requiredPermissions: ['module_config.read']
  },
  {
    id: 'admin-module-form',
    name: 'Form & stage builder',
    link: '/admin/modules',
    icon: SettingsIcon,
    allowedRoles: ['ROLE_SUPER_ADMIN']
  },

  // ── 9. Settings ────────────────────────────────────────────────────
  // The one titled section. Communication settings live on the NotificationList
  // page (/notifications); Project Settings is the tenant branding page
  // (/branding); My profile is the personal account page.
  {
    id: 'communication',
    name: 'Communication',
    link: '/communications/calendar',
    icon: NotificationsIcon,
    allowedRoles: ADMIN_LIKE,
    requiredModule: 'COMMUNICATION',
    // `/notifications` now has its own "Communication profile & settings" entry
    // in the Settings section, so it's no longer aliased to this item.
    matchPaths: [
      '/communications/project-updates',
      '/communications/templates'
    ],
    section: 'settings'
  },
  {
    id: 'branding',
    name: 'Project Settings',
    link: '/branding',
    icon: SettingsIcon,
    allowedRoles: ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_DEVELOPER'],
    requiredModule: 'BRANDING',
    section: 'settings'
  },
  {
    id: 'profile',
    name: 'My profile',
    link: '/profile',
    icon: HelpCircleIcon,
    // No allowedRoles → visible to every authenticated user. Also offered from
    // the header's avatar menu, so it's reachable either way.
    section: 'settings'
  }
]

export function getMenuItemByPath(
  pathname: string,
  role?: string
): MenuItem | undefined {
  const findItem = (items: MenuItem[]): MenuItem | undefined => {
    for (const item of items) {
      if (item.link && pathname.startsWith(item.link)) {
        return item
      }
      if (item.children) {
        const found = findItem(item.children)
        if (found) return found
      }
    }
    return undefined
  }

  const item = findItem(menuItems)
  if (item) return item

  if (pathname === '/dashboard') {
    return menuItems.find((i) => i.id === 'dashboard')
  }

  const pathParts = pathname.split('/').filter(Boolean)
  if (pathParts.length > 0) {
    const moduleName = pathParts[0]
    const formattedName = moduleName
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
    return {
      id: moduleName,
      name: formattedName,
      link: pathname,
      icon: () => null,
      allowedRoles: []
    } as unknown as MenuItem
  }

  return undefined
}

export function getAllMenuItems() {
  return menuItems
}

export default menuItems
